import { useCallback, useEffect, useState } from 'react'
import { CircleX } from 'lucide-react'
import { useAuth } from '../auth'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { supabase } from '../lib/supabase'
import { darf } from '../lib/rollen'
import { aboKuendigen, kuendigungZuruecknehmen, kuendigungsDatum, kuendigungsVorschau, type KuendigungsVorschau } from '../data/kuendigung'
import { kuendigungTexte } from '../locales/kuendigung'
import { Button, Field, Input, Sheet } from '../ui/kit'

export function AboKuendigenEintrag() {
  const { mode, rolle } = useAuth()
  const { lang } = useI18n()
  const [offen, setOffen] = useState(false)
  if (mode !== 'live' || !darf(rolle, 'abrechnung')) return null
  return <>
    <Button variant="ghost" onClick={() => setOffen(true)}><CircleX size={16} />{kuendigungTexte[lang].entry}</Button>
    {offen && <AboKuendigenSheet onClose={() => setOffen(false)} />}
  </>
}

export function AboKuendigenSheet({ onClose }: { onClose: () => void }) {
  const { lang } = useI18n()
  const { db } = useStore()
  const text = kuendigungTexte[lang]
  const [vorschau, setVorschau] = useState<KuendigungsVorschau | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [grund, setGrund] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [erfolg, setErfolg] = useState<string | null>(null)
  const laden = useCallback(async () => {
    setLoading(true); setFehler(null)
    try { setVorschau(await kuendigungsVorschau(supabase, db.tenant.id)) }
    catch { setVorschau(null); setFehler(text.unavailable) }
    finally { setLoading(false) }
  }, [db.tenant.id, text.unavailable])
  useEffect(() => { void laden() }, [laden])
  const handeln = async (widerruf: boolean) => {
    setBusy(true); setFehler(null); setErfolg(null)
    try {
      const r = widerruf ? await kuendigungZuruecknehmen(supabase, db.tenant.id) : await aboKuendigen(supabase, db.tenant.id, grund)
      if (!r.geschrieben && !['bereits_gekuendigt', 'keine_kuendigung'].includes(r.ergebnis)) throw new Error(r.ergebnis)
      await laden()
      setErfolg(widerruf ? text.undone : text.success)
    } catch (err) {
      if (err instanceof Error && err.message === 'crm_datum_nicht_freigegeben') {
        setVorschau(v => v ? { ...v, abschluss_freigegeben: false } : null)
        setFehler(null)
      } else { setVorschau(null); setFehler(text.failed) }
    }
    finally { setBusy(false) }
  }
  const support = <div className="space-y-2"><p>{text.support}</p><a className="underline" href="mailto:support@auviso.com?subject=StaySuite%20Abo-K%C3%BCndigung">{text.supportLink}</a></div>
  const store = vorschau?.kanal === 'apple' || vorschau?.kanal === 'google'
  return <Sheet open title={text.title} onClose={busy ? () => {} : onClose}>
    <div className="space-y-4">
      {loading && <p>{text.loading}</p>}
      {fehler && <div role="alert" className="text-danger space-y-2"><p>{fehler}</p><Button variant="secondary" disabled={busy} onClick={() => void laden()}>{text.retry}</Button></div>}
      {erfolg && <p role="status" className="text-accent">{erfolg}</p>}
      {vorschau && !loading && (store ? <>
        <p>{text.store}</p>
        <a className="underline" href={vorschau.kanal === 'apple' ? 'https://apps.apple.com/account/subscriptions' : 'https://play.google.com/store/account/subscriptions'} target="_blank" rel="noopener noreferrer">{vorschau.kanal === 'apple' ? text.apple : text.google}</a>
      </> : vorschau.beendet && (vorschau.abschluss_freigegeben === true || vorschau.status === 'canceled') ? <p>{text.ended}</p> : vorschau.konto_loeschung_laeuft ? <p>{text.deletion}</p> : vorschau.abschluss_freigegeben !== true ? <>
        {support}
        {vorschau.bereits_gekuendigt && vorschau.wirksam_am && <p>{text.pending} {kuendigungsDatum(vorschau.wirksam_am, lang)}.</p>}
        {vorschau.bereits_gekuendigt && vorschau.widerruf_moeglich && <Button disabled={busy} onClick={() => void handeln(true)}>{text.withdraw}</Button>}
      </> : <>
        {vorschau.wirksam_am && <p className="font-semibold">{vorschau.bereits_gekuendigt ? text.pending : text.effective} {kuendigungsDatum(vorschau.wirksam_am, lang)}.</p>}
        <p>{text.unchanged}</p><p>{text.consequence}</p><p>{text.withdrawal}</p>
        {vorschau.bereits_gekuendigt ? vorschau.widerruf_moeglich && <Button disabled={busy} onClick={() => void handeln(true)}>{text.withdraw}</Button> : <>
          <Field label={text.reason}><Input maxLength={2000} value={grund} onChange={e => setGrund(e.target.value)} /></Field>
          <p className="text-xs text-muted">{text.reasonHint}</p>
          <Button variant="danger" disabled={busy || !vorschau.wirksam_am || !!fehler} onClick={() => void handeln(false)}>{text.confirm}</Button>
        </>}
      </>)}
      <Button variant="secondary" disabled={busy} onClick={onClose}>{text.close}</Button>
    </div>
  </Sheet>
}
